'use client';

import moment from 'moment';
import React, { useCallback } from 'react';
import { Button, Table } from 'reactstrap';

import OnSiteQuoteApprovalTimer from 'src/components/shared/on-site-quote-approval-timer';

import { useAppSelector } from 'src/hooks/redux-hooks';
import { updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { IJobs } from 'src/types/jobs';

interface QuoteColumn {
  key: string;
  label: string;
  render: (row: IJobs) => React.ReactNode;
  tableHeadClass?: string;
  tableTdClass?: string;
}

const quotesStatus: Record<string, string> = {
  approved: 'approved',
  expired: 'expired',
  pending_approval: 'pending-approval',
  rejected: 'rejected',
  awaiting_funds: 'awaiting-funds',
  on_site_approval: 'on-site-approval',
};

const Quotes: React.FC = () => {
  const quotes = useAppSelector(
    (state) => state?.jobDetails?.data?.quotes,
  );
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);

  const quoteColumns = useCallback((): QuoteColumn[] => [
    {
      key: 'quote_no',
      label: 'QUOTE #',
      render: (row) => row?.quote_no || '',
    },
    ...(type === 'serviceRequestDetails'
      ? [
        {
          key: 'job_id',
          label: 'LULA JOB #',
          render: (row: IJobs) => (
            <Button
              className="btn-link"
              color="btn-deoco"
              onClick={() => updateUrlForOpenJobSidePanel(
                row?.root_job_no ?? '',
                'jobDetails',
                row?.root_job_id ?? '',
                row?.service_request_id ?? '',
              )}
            >
              {row?.quote_no || ''}
            </Button>
          ),
          tableHeadClass: 'lula-job',
        },
      ]
      : []),
    {
      key: 'service_vertical',
      label: 'SERVICE VERTICAL',
      render: (row) => row?.service_category_label,
      tableHeadClass: 'service-vertical',
      tableTdClass: 'service-vertical',
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (row) => (
        <React.Fragment>
          <div className={`status ${quotesStatus?.[row?.status?.slug || '']}`}>
            {row?.status?.label || ''}
          </div>
          {row?.status?.slug === 'on_site_approval' && row?.onsite_quote_expires_at
            ? (
              <OnSiteQuoteApprovalTimer
                quoteExpiryAt={row?.onsite_quote_expires_at}
                className="onsite-quote-approval-timer"
              />
            )
            : ''}
        </React.Fragment>
      ),
    },
    {
      key: 'amount',
      label: 'AMOUNT',
      render: (row) => `$${row?.amount}` || '',
      tableTdClass: 'amount',
    },
    {
      key: 'submit_date',
      label: 'SUBMIT DATE',
      render: (row) => (row?.submit_date
        ? moment.utc(row?.submit_date).local().format('MMM DD, YYYY')
        : '-'),
      tableTdClass: 'submit-date',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (row?.quote_review_url ? (
        <Button
          className="btn-link"
          color="btn-deoco"
          onClick={() => {
            window.open(row?.quote_review_url, '_blank');
          }}
        >
          Review
        </Button>
      ) : (
        ''
      )),
    },
  ], [type]);

  return (
    quotes?.length
      ? (
        <Table responsive className="job-content-table">
          <thead>
            <tr>
              {quoteColumns().map((column) => (
                <th key={column?.key} className={column?.tableHeadClass || ''}>
                  {column?.label}
                </th>
              )) }
            </tr>
          </thead>
          <tbody>
            {Array?.isArray(quotes)
              ? quotes?.map((rowData) => (
                <tr key={rowData?.job_id}>
                  {quoteColumns()?.map((column) => (
                    <td
                      key={`${column?.key}`}
                      className={
                        column?.tableTdClass || ''
                      }
                    >
                      {column?.render(rowData)}
                    </td>
                  ))}
                </tr>
              )) : ''}
          </tbody>
        </Table>
      )
      : (
        <div className="no-data mt-3">
          No quotes have been submitted for this job
        </div>
      )
  );
};
export default Quotes;
